import last from 'lodash/last';
import round from 'lodash/round';
import { CSSProperties } from 'styled-components';

import { Icon } from '~src/components/Icon';
import { JYSKE_HEATPUMP_LOAN, NORDEA_HEATPUMP_LOAN } from '~src/constants';
import { useDisplayContext } from '~src/context/DisplayContext';
import { usePublicCalculatorContext } from '~src/context/PublicCalculatorContext';
import { DeepPartial, NewOfferExpenses, OfferExpense, OfferProducts, Residence, TakeoverType } from '~src/model';
import {
  calculateOfferPrice,
  calculatePaybackTime,
  findLoanCharacteristics,
  formatCurrency,
  formatNumber,
  isResidenceFilled,
  withVAT,
} from '~src/util';

import { useNewOfferPriceWithExpenses } from './useNewOfferPriceWithExpenses';
import { useOfferSavings } from './useOfferSavings';

export type SavingsProps = {
  residence?: DeepPartial<Residence>;
  expenses?: OfferExpense[];
  savingsAfterYears?: number;
  style?: CSSProperties;
  compact?: boolean;
  products?: OfferProducts;
  newOfferExpenses?: NewOfferExpenses;
  wrapperStyle?: CSSProperties;
  takeover?: TakeoverType;
};

export const useSavingsEstimate = ({
  residence,
  savingsAfterYears = 15,
  newOfferExpenses,
  compact,
  products,
}: SavingsProps) => {
  const { financing } = usePublicCalculatorContext();
  const { leadOrigin } = useDisplayContext();

  const savings = useOfferSavings(products, residence, ['heatPump']);

  const expensesPrice = useNewOfferPriceWithExpenses(products, newOfferExpenses);

  // Empty financing result
  if (!savings || !residence || !isResidenceFilled(residence)) {
    return {
      entries: formatEntries(
        {
          savedPerYear: 0,
          totalPrice: 0,
          emissionSavedPerYear: 0,
          finalSaving: { years: savingsAfterYears, total: 0 },
          paybackFormatted: '0 år',
        },
        compact
      ),
    };
  }

  const { saved } = savings;

  const { price: savedPerYear, emission: emissionSavedPerYear, priceByYear } = saved;

  const totalPrice = withVAT(calculateOfferPrice(products) + expensesPrice);

  // Single Jyske or Nordea Loan
  const loan = (leadOrigin ?? '') === 'jyskebank' ? JYSKE_HEATPUMP_LOAN : NORDEA_HEATPUMP_LOAN;

  const { paymentPerMonth, totalPaid } = financing
    ? findLoanCharacteristics(totalPrice, loan)
    : { paymentPerMonth: 0, totalPaid: totalPrice };

  const paymentPerYear = round(paymentPerMonth * 12, 2);

  const paybackFormatted = financing
    ? `${loan.totalYears} år`
    : calculatePaybackTime(totalPrice, years => priceByYear[years] ?? last(priceByYear) ?? 0).paybackFormatted;

  const totalSaving = savedPerYear * savingsAfterYears - totalPaid;

  return {
    entries: formatEntries(
      {
        paymentPerYear: financing ? paymentPerYear : 0,
        finalSaving: { years: savingsAfterYears, total: totalSaving },
        savedPerYear: financing ? savedPerYear - paymentPerYear : savedPerYear,
        totalPrice: financing ? 0 : totalPrice,
        emissionSavedPerYear,
        paybackFormatted,
      },
      compact
    ),
  };
};

type EntriesInput = {
  savedPerYear: number;
  paymentPerYear?: number;
  finalSaving: { years: number; total: number };
  totalPrice: number;
  emissionSavedPerYear: number;
  paybackFormatted: string;
};

const formatEntries = (input: EntriesInput, compact = false) => {
  const { totalPrice, paymentPerYear, finalSaving, emissionSavedPerYear, paybackFormatted, savedPerYear } = input;

  const investmentEntry = {
    label: 'Pris',
    value: formatCurrency(totalPrice, { decimals: 0 }),
    Icon: Icon.PriceTag,
  };

  const savingsEntry = {
    label: `Årlig besparelse ${paymentPerYear ? '(inkl. ydelse)' : ''}`,
    value: formatCurrency(savedPerYear, { decimals: 0 }),
    Icon: Icon.PiggyBank,
  };

  if (compact) {
    return [investmentEntry, savingsEntry];
  }

  const paymentEntry = paymentPerYear
    ? {
        label: 'Årlig ydelse',
        value: formatCurrency(paymentPerYear, { decimals: 0 }),
        Icon: Icon.PriceTag,
      }
    : undefined;

  return [
    investmentEntry,
    ...(paymentEntry ? [paymentEntry] : []),
    savingsEntry,
    {
      label: `Gevinst efter ${finalSaving.years} år`,
      value: formatCurrency(finalSaving.total, { decimals: 0 }),
      Icon: Icon.PriceTag,
    },
    {
      label: 'Årlig CO2 besparelse',
      value: `${formatNumber(1e-3 * (emissionSavedPerYear ?? 0))} t`,
      Icon: Icon.CO2,
    },
    {
      label: 'Tilbagebetalingstid',
      value: paybackFormatted ?? '0 år',
      Icon: Icon.Finance,
    },
  ];
};
